import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { TabbedView } from 'components/List';
import SearchablePagesTable from 'components/SearchablePagesTable';
import User from 'shared/user';
import { BUILDER, FACILITY_PAGES, NON_FACILITY_PAGES, DRAFT } from 'shared/abilities';
import { HOME_PAGE, uniquePageTypes } from 'shared/pageTypes';
import ActionModal from '../ActionModal';
import FacilityIdModal from '../FacilityIdModal';
import ApplyPageLayoutModal from '../../../../../../admin/containers/ApplyPageLayout';
import ConfirmReplaceModal from '../ConfirmReplaceModal';
import Language from '../../containers/Language';
import PageStatus from '../../containers/PageStatus';
import PageDeleteModal from '../../../../../../admin/containers/PageDeleteModal';
import {
  PUBLISH_ACTION,
  DRAFT_ACTION,
  LIVE_ACTION,
  CLONE_ACTION,
  MULTI_LANG_CLONE_MESSAGE,
  MULTI_LANG_CLONE_BODY,
  MULTI_LANG_PUBLISH_MESSAGE,
  MULTI_LANG_DRAFT_MESSAGE,
  DRAFT_BODY,
  MULTI_LANG_LIVE_MESSAGE,
  LIVE_MESSAGE,
  LIVE_BODY,
} from './constants/actions';
import style from './PageList.scss';
import generateFilters from './filters';
import { PageListActions, PageTitle, PageType, LastUpdated, ViewLive } from '.';

export default class PageList extends React.Component {
  static propTypes = {
    clonePage: PropTypes.func.isRequired,
    pages: PropTypes.array,
    search: PropTypes.func.isRequired,
    updateFilters: PropTypes.func.isRequired,
    selectedFilters: PropTypes.object.isRequired,
    savePage: PropTypes.func.isRequired,
    saveAndReloadPages: PropTypes.func.isRequired,
    publishPage: PropTypes.func.isRequired,
    pushRoute: PropTypes.func.isRequired,
    profile: PropTypes.object.isRequired,
    onlyShowAuthenticated: PropTypes.bool.isRequired,
    toggleAuthenticatedPages: PropTypes.func.isRequired,
    languagesEnabled: PropTypes.bool,
    nonDefaultLanguages: PropTypes.array,
    makeDraft: PropTypes.func.isRequired,
    makeLive: PropTypes.func.isRequired,
    switchLiveAndDraft: PropTypes.func.isRequired,
    switchFacilityId: PropTypes.func.isRequired,
    builderListViewFeature: PropTypes.bool,
    clearFilters: PropTypes.func,
    breadcrumbTrailsEnabled: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      checkedIds: [],
      actionName: undefined,
      showModal: false,
      confirmed: false,
      currPage: undefined,
      pageToDraft: undefined,
      showFacilityIdModal: false,
      showConfirmReplaceModal: false,
      showDeleteModal: false,
      showApplyPageLayoutModal: false,
    };
  }

  onCheckChange = (checkedIds) => {
    this.setState({ checkedIds });
  }

  onActionDismiss = () => {
    this.setState({ showModal: false });
  }

  onActionConfirm = () => {
    this.setState({ confirmed: true });
  }

  handleClone = (page) => {
    this.props.clonePage(page);
    this.onActionConfirm();
  }

  handlePublish = (page) => {
    this.props.publishPage(page.id, page.version.id);
    this.onActionConfirm();
  }

  handleDraft = (page) => {
    this.props.makeDraft(page);
    this.onActionConfirm();
  }

  handleDeleteModal = page => () => {
    if (page && page.id) this.setState({ showDeleteModal: true, currPage: page });
  }

  handleDeleteModalClose = () => {
    this.setState({ showDeleteModal: false, currPage: undefined });
  }

  handleLive = (pageToMakeLive) => {
    this.props.makeLive(pageToMakeLive);
    this.onActionConfirm();
  }

  switcheroo = () => {
    this.props.switchLiveAndDraft(this.state.currPage, this.state.pageToDraft);
    this.setState({
      pageToDraft: undefined,
      showConfirmReplaceModal: false,
    });
  }

  handleActionModal = ({ actionName, currPage }) => () => {
    const { unpublishedChanges, live } = currPage;

    if ((!unpublishedChanges && actionName === PUBLISH_ACTION) ||
        (!live && actionName === DRAFT_ACTION) ||
        (live && actionName === LIVE_ACTION)) {
      return;
    }

    if (actionName === LIVE_ACTION) {
      this.handleLiveAction(actionName, currPage);
      return;
    }

    this.setState({
      actionName,
      currPage,
      showModal: true,
      confirmed: false,
    });
  }

  handleLiveAction(actionName, pageToMakeLive) {
    const { pages } = this.props;
    const livePages = pages.filter(page => page.live);
    let clonedLivePage = false;

    if (pageToMakeLive.facility) {
      clonedLivePage = pages.reduce((acc, page) => {
        if (page.facility && page.facility.id === pageToMakeLive.facility.id && page.live) {
          return page.live;
        }
        return acc;
      }, false);
    }

    if (!uniquePageTypes.includes(pageToMakeLive.metadata.type) && !clonedLivePage) {
      return this.setState({
        actionName,
        currPage: pageToMakeLive,
        showModal: true,
        confirmed: false,
      });
    }

    // there may only be one live page per facility, but multiple facility pages can be live
    if (pageToMakeLive.facilityId) {
      const filteredLive = livePages.find(page => (page.facilityId
        && page.facilityId === pageToMakeLive.facilityId && page.metadata.type !== HOME_PAGE));
      if (!filteredLive) {
        return this.setState({
          actionName,
          currPage: pageToMakeLive,
          showModal: true,
          confirmed: false,
        });
      }
      return this.setState({
        currPage: pageToMakeLive,
        pageToDraft: filteredLive,
        showConfirmReplaceModal: true,
      });
    }

    // for all other page types, only one may be live
    const toDraft = livePages.find(page => page.metadata.type === pageToMakeLive.metadata.type);
    if (toDraft) {
      return this.setState({
        currPage: pageToMakeLive,
        pageToDraft: toDraft,
        showConfirmReplaceModal: true,
      });
    }

    return this.setState({
      actionName,
      currPage: pageToMakeLive,
      showModal: true,
      confirmed: false,
    });
  }

  handleFacilityIdModal = page => () => {
    this.setState({ showFacilityIdModal: true, currPage: page });
  }

  handleApplyPageLayout = page => () => {
    this.setState({ showApplyPageLayoutModal: true, currPage: page });
  }

  handleFacilityIdChange = ({ facilityId }, livePage) => {
    const { switchFacilityId } = this.props;
    const { currPage } = this.state;

    switchFacilityId(
      { ...currPage, facilityId },
      livePage,
    );

    this.setState({ showFacilityIdModal: false, currPage: undefined });
  }

  handleFacilityIdChangeClose = () => {
    this.setState({ showFacilityIdModal: false, currPage: undefined });
  }

  handleSettings = page => () => {
    this.props.pushRoute(`admin/settings/pages?page=${page.id}`);
  }

  handleEdit = page => () => {
    this.props.pushRoute(`/admin/page/edit/${page.id}`);
  }

  actionModalProps = () => {
    const { languagesEnabled } = this.props;
    const { actionName, currPage } = this.state;

    const actions = {
      [CLONE_ACTION]: {
        name: CLONE_ACTION,
        ...(languagesEnabled ? {
          message: MULTI_LANG_CLONE_MESSAGE,
          body: MULTI_LANG_CLONE_BODY,
        } : {}),
        onConfirm: () => this.handleClone(currPage),
      },
      [PUBLISH_ACTION]: {
        name: PUBLISH_ACTION,
        ...(languagesEnabled ? {
          message: MULTI_LANG_PUBLISH_MESSAGE,
        } : {}),
        onConfirm: () => this.handlePublish(currPage),
      },
      [DRAFT_ACTION]: {
        name: DRAFT_ACTION,
        ...(languagesEnabled ? {
          message: MULTI_LANG_DRAFT_MESSAGE,
        } : {}),
        body: DRAFT_BODY,
        confirmationButton: 'Make Draft',
        onConfirm: () => this.handleDraft(currPage),
      },
      [LIVE_ACTION]: {
        title: 'Make Page Live and Publish',
        name: LIVE_ACTION,
        ...(languagesEnabled ? {
          message: MULTI_LANG_LIVE_MESSAGE,
        } : {
          message: LIVE_MESSAGE,
        }),
        body: LIVE_BODY,
        confirmationButton: 'Make Live',
        onConfirm: () => this.handleLive(currPage),
      },
    };

    return actions[actionName];
  }

  pageListActionsComponent = (page) => {
    const { profile } = this.props;

    const pageListActionProps = {
      page,
      handleEdit: this.handleEdit,
      handleSettings: this.handleSettings,
      handleActionModal: this.handleActionModal,
      handlePopOvers: this.handlePopOvers,
      handleFacilityIdModal: this.handleFacilityIdModal,
      handleDeleteModal: this.handleDeleteModal,
      profile,
      disabledRow: !this.rowAuth(page),
      handleApplyPageLayout: this.handleApplyPageLayout,
    };

    return (
      <PageListActions {...pageListActionProps} />
    );
  }

  generateColumns = () => {
    const { languagesEnabled, nonDefaultLanguages } = this.props;

    let columns = [
      {
        title: 'Status',
        component: PageStatus,
        style: style.centered,
      },
      {
        title: 'Page',
        component: PageTitle,
      },
      {
        title: 'Type',
        component: PageType,
      },
      {
        title: 'Language',
        component: Language,
        renderCondition: () => languagesEnabled && nonDefaultLanguages.length,
      },
      {
        title: 'Last Updated',
        component: LastUpdated,
      },
      {
        title: 'View Live',
        component: ViewLive,
        style: style.viewLiveColumn,
        noAuth: true,
      },
      {
        component: this.pageListActionsComponent,
        noAuth: true,
      },
    ];

    columns = columns.filter(column => (column.renderCondition ? column.renderCondition() : true));

    return columns;
  }

  rowAuth = (page) => {
    const { profile } = this.props;
    const user = new User(profile);

    const { facilityId } = page;
    const subject = page.facility ? [FACILITY_PAGES, BUILDER] : [NON_FACILITY_PAGES, BUILDER];
    const { companyId } = user;

    return user.isAuthorized({
      action: 'manage', subject, facilityId, companyId,
    });
  };

  renderrightOfSearch() {
    const { onlyShowAuthenticated, toggleAuthenticatedPages } = this.props;
    return [props =>
      props.disabledRowCount !== 0 &&
        <div className={`${style.checkBox} col-xs-6`} key="onlyMyPages">
          <label>
            <input
              type="checkbox"
              onChange={toggleAuthenticatedPages}
              defaultChecked={onlyShowAuthenticated}
            />
            <span className={style.checkBoxText}>
              View only my pages
            </span>
          </label>
        </div>,
    ];
  }

  render = () => {
    const {
      pages,
      search,
      updateFilters,
      selectedFilters,
      onlyShowAuthenticated,
      toggleAuthenticatedPages,
      profile,
      builderListViewFeature,
      clearFilters,
    } = this.props;
    const {
      showModal,
      confirmed,
      currPage,
      showFacilityIdModal,
      showConfirmReplaceModal,
      checkedIds,
      showDeleteModal,
      showApplyPageLayoutModal,
    } = this.state;

    const user = new User(profile);
    const livePages = pages.filter(page => page.live);
    let filtered = pages;

    if (!user.isAuthorized({
      action: 'manage',
      subject: [BUILDER, DRAFT],
      companyId: user.companyId,
    })) {
      filtered = filtered.filter(page => page.live);
    }
    const pagesByDateDesc = filtered.sort((a, b) => {
      const aCreatedAt = a.versions[0]?.createdAt;
      const bCreatedAt = b.versions[0]?.createdAt;

      // Handle cases where versions array is empty or createdAt is undefined
      if (!a.versions.length || !b.versions.length || !aCreatedAt || !bCreatedAt) {
        return 0; // or handle differently based on your requirements
      }

      return moment(bCreatedAt).diff(moment(aCreatedAt));
    });

    const action = this.actionModalProps();
    const facility = currPage ? currPage.facility : {};

    const searchableTableProps = {
      columns: this.generateColumns(),
      source: pagesByDateDesc,
      search,
      updateFilters,
      selectedFilters,
      enableCheckboxes: builderListViewFeature,
      rightOfSearch: this.renderrightOfSearch(),
      onCheckChange: this.onCheckChange,
      filters: generateFilters(this.props),
      rowAuth: this.rowAuth,
      onlyShowAuthenticated,
      toggleAuthenticatedPages,
      rowAuthMsg: "You don't have permission to edit this page.",
      checkedIds,
      builderListViewFeature,
      clearFilters,
    };

    return (
      <React.Fragment>
        <TabbedView showTabs={false}>
          <SearchablePagesTable {...searchableTableProps} />
        </TabbedView>
        <React.Fragment>
          <ActionModal
            {...action}
            show={!!showModal}
            confirmed={confirmed}
            onClose={this.onActionDismiss}
          />
        </React.Fragment>
        <React.Fragment>
          {showFacilityIdModal &&
            <FacilityIdModal
              live={currPage.live}
              livePages={livePages}
              facility={facility}
              onSubmit={this.handleFacilityIdChange}
              onClose={this.handleFacilityIdChangeClose}
            />
          }
          {showDeleteModal &&
            <PageDeleteModal
              show={showDeleteModal}
              page={currPage}
              onDismiss={this.handleDeleteModalClose}
              search={search}
            />
          }
          {showConfirmReplaceModal &&
            <ConfirmReplaceModal
              onClose={() => this.setState({ showConfirmReplaceModal: false })}
              onConfirm={this.switcheroo}
            />
          }
          {showApplyPageLayoutModal &&
            <ApplyPageLayoutModal
              onClose={() => this.setState({ showApplyPageLayoutModal: false })}
              page={currPage}
            />
          }
        </React.Fragment>
      </React.Fragment>
    );
  }
}
